/**
 * Modal for editing a chat group (name, description, members).
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Stack,
  Alert,
  Checkbox,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Avatar,
  Box,
  CircularProgress,
  Chip,
  IconButton,
  Divider,
  Tabs,
  TabList,
  Tab,
  TabPanel,
} from '@mui/joy';
import { MagnifyingGlass as SearchIcon, User as PersonIcon, UserMinus as PersonRemoveIcon, Star as StarIcon } from '@phosphor-icons/react';
import { useOrganizationUsers } from '../../hooks/useOrganizationUsers';
import {
  useChatGroup,
  useUpdateChatGroup,
  useAddGroupMembers,
  useRemoveGroupMember,
} from '../../hooks/useChat';

// Get first and last initials from a name (e.g., "Jeffrey Stilwell" -> "JS")
const getInitials = (name: string | undefined): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0][0]?.toUpperCase() || '?';
  }
  const first = parts[0][0]?.toUpperCase() || '';
  const last = parts[parts.length - 1][0]?.toUpperCase() || '';
  return first + last;
};

// Generate a consistent color based on a string (name/username)
const AVATAR_COLORS = [
  '#7C3AED', // violet
  '#2563EB', // blue
  '#0891B2', // cyan
  '#059669', // emerald
  '#D97706', // amber
  '#DC2626', // red
  '#DB2777', // pink
  '#7C3AED', // purple
];

const getAvatarColor = (name: string | undefined): string => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

interface EditGroupModalProps {
  open: boolean;
  onClose: () => void;
  groupId: string;
  currentUserId?: string | number;
}

const EditGroupModal: React.FC<EditGroupModalProps> = ({
  open,
  onClose,
  groupId,
  currentUserId,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data: group, isLoading: groupLoading, refetch } = useChatGroup(groupId);
  const { users, loading: usersLoading } = useOrganizationUsers();
  const updateGroup = useUpdateChatGroup();
  const addMembers = useAddGroupMembers();
  const removeMember = useRemoveGroupMember();

  // Initialize form when group data loads
  useEffect(() => {
    if (group) {
      setName(group.name || '');
      setDescription(group.description || '');
    }
  }, [group]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setActiveTab(0);
      setSearchQuery('');
      setSelectedUserIds([]);
      setError(null);
      setSuccessMessage(null);
    }
  }, [open]);

  // Get current member IDs
  const currentMemberIds = group?.members?.map((m) => String(m.id)) || [];
  const adminIds = group?.admins?.map((a) => String(a.id)) || [];

  // Filter users for adding (exclude current members)
  const availableUsers = users.filter((user) => {
    if (currentMemberIds.includes(String(user.id))) {
      return false;
    }
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.full_name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.first_name?.toLowerCase().includes(query) ||
      user.last_name?.toLowerCase().includes(query)
    );
  });

  const toggleUser = (userId: number) => {
    const idStr = String(userId);
    setSelectedUserIds((prev) =>
      prev.includes(idStr) ? prev.filter((id) => id !== idStr) : [...prev, idStr]
    );
  };

  const getSelectedUsers = () => {
    return users.filter((user) => selectedUserIds.includes(String(user.id)));
  };

  const isAdmin = currentUserId && adminIds.includes(String(currentUserId));

  // Handle updating group details
  const handleUpdateDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!name.trim()) {
      setError('Group name is required');
      return;
    }

    try {
      await updateGroup.mutateAsync({
        groupId,
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
        },
      });
      setSuccessMessage('Group updated successfully');
      refetch();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update group';
      setError(errorMessage);
    }
  };

  // Handle adding members
  const handleAddMembers = async () => {
    if (selectedUserIds.length === 0) return;

    setError(null);
    setSuccessMessage(null);

    try {
      await addMembers.mutateAsync({
        groupId,
        data: { user_ids: selectedUserIds },
      });
      setSelectedUserIds([]);
      setSearchQuery('');
      setSuccessMessage(`Added ${selectedUserIds.length} member(s)`);
      refetch();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add members';
      setError(errorMessage);
    }
  };

  // Handle removing a member
  const handleRemoveMember = async (userId: string) => {
    setError(null);
    setSuccessMessage(null);

    try {
      await removeMember.mutateAsync({ groupId, userId });
      setSuccessMessage('Member removed');
      refetch();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove member';
      setError(errorMessage);
    }
  };

  if (groupLoading) {
    return (
      <Modal open={open} onClose={onClose}>
        <ModalDialog sx={{ minWidth: 500 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </ModalDialog>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ minWidth: 500, maxHeight: '85vh', overflow: 'hidden' }}>
        <ModalClose />
        <Typography level="h4" sx={{ mb: 2 }}>
          Edit Group: {group?.name}
        </Typography>

        {(error || successMessage) && (
          <Alert
            color={error ? 'danger' : 'success'}
            variant="soft"
            sx={{ mb: 2 }}
          >
            {error || successMessage}
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value as number)}
          sx={{ mb: 2 }}
        >
          <TabList>
            <Tab>Details</Tab>
            <Tab>
              Members ({group?.members?.length || 0})
            </Tab>
            <Tab>Add Members</Tab>
          </TabList>

          {/* Details Tab */}
          <TabPanel value={0} sx={{ p: 0, pt: 2 }}>
            <form onSubmit={handleUpdateDetails}>
              <Stack spacing={2}>
                <FormControl required>
                  <FormLabel>Group Name</FormLabel>
                  <Input
                    placeholder="e.g., Project Team, Sales"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!isAdmin}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    placeholder="What's this group for?"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    minRows={2}
                    disabled={!isAdmin}
                  />
                </FormControl>

                {isAdmin && (
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      type="submit"
                      loading={updateGroup.isPending}
                      disabled={!name.trim()}
                    >
                      Save Changes
                    </Button>
                  </Stack>
                )}

                {!isAdmin && (
                  <Typography level="body-sm" color="neutral">
                    Only group admins can edit details.
                  </Typography>
                )}
              </Stack>
            </form>
          </TabPanel>

          {/* Members Tab */}
          <TabPanel value={1} sx={{ p: 0, pt: 2 }}>
            <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
              <List size="sm">
                {group?.members?.map((member) => {
                  const isMemberAdmin = adminIds.includes(String(member.id));
                  const isCreator = group.created_by?.id === member.id;
                  const canRemove = isAdmin && !isCreator && String(member.id) !== String(currentUserId);

                  return (
                    <ListItem
                      key={member.id}
                      endAction={
                        canRemove ? (
                          <IconButton
                            size="sm"
                            color="danger"
                            variant="plain"
                            onClick={() => handleRemoveMember(String(member.id))}
                            disabled={removeMember.isPending}
                          >
                            <PersonRemoveIcon size={18} />
                          </IconButton>
                        ) : null
                      }
                    >
                      <ListItemDecorator>
                        <Avatar
                          size="sm"
                          src={member.avatar_url || undefined}
                          sx={{
                            bgcolor: member.avatar_url ? undefined : getAvatarColor(member.full_name || member.username),
                            color: 'white',
                          }}
                        >
                          {getInitials(member.full_name || member.username)}
                        </Avatar>
                      </ListItemDecorator>
                      <ListItemContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography level="body-sm">
                            {member.full_name || member.username}
                          </Typography>
                          {isMemberAdmin && (
                            <Chip
                              size="sm"
                              variant="soft"
                              color="warning"
                              startDecorator={<StarIcon size={12} />}
                            >
                              Admin
                            </Chip>
                          )}
                          {isCreator && (
                            <Chip size="sm" variant="soft" color="primary">
                              Creator
                            </Chip>
                          )}
                          {String(member.id) === String(currentUserId) && (
                            <Chip size="sm" variant="outlined" color="neutral">
                              You
                            </Chip>
                          )}
                        </Box>
                        <Typography level="body-xs" color="neutral">
                          {member.email}
                        </Typography>
                      </ListItemContent>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </TabPanel>

          {/* Add Members Tab */}
          <TabPanel value={2} sx={{ p: 0, pt: 2 }}>
            {!isAdmin ? (
              <Typography level="body-sm" color="neutral" sx={{ py: 2 }}>
                Only group admins can add members.
              </Typography>
            ) : (
              <Stack spacing={2}>
                <Input
                  placeholder="Search users..."
                  startDecorator={<SearchIcon />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />

                {selectedUserIds.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {getSelectedUsers().map((user) => (
                      <Chip
                        key={user.id}
                        size="sm"
                        variant="soft"
                        color="primary"
                        endDecorator={
                          <Box
                            component="span"
                            sx={{ cursor: 'pointer', ml: 0.5 }}
                            onClick={() => toggleUser(user.id)}
                          >
                            x
                          </Box>
                        }
                      >
                        {user.full_name || user.email}
                      </Chip>
                    ))}
                  </Box>
                )}

                <Box
                  sx={{
                    maxHeight: 200,
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 'sm',
                  }}
                >
                  {usersLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size="sm" />
                    </Box>
                  ) : availableUsers.length === 0 ? (
                    <Typography
                      level="body-sm"
                      sx={{ textAlign: 'center', color: 'text.secondary', p: 2 }}
                    >
                      {searchQuery ? 'No users found' : 'All users are already members'}
                    </Typography>
                  ) : (
                    <List size="sm">
                      {availableUsers.map((user) => (
                        <ListItem key={user.id}>
                          <Checkbox
                            overlay
                            checked={selectedUserIds.includes(String(user.id))}
                            onChange={() => toggleUser(user.id)}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar size="sm">
                                  {user.first_name?.[0] || user.email?.[0] || <PersonIcon />}
                                </Avatar>
                                <Box>
                                  <Typography level="body-sm">
                                    {user.full_name ||
                                      `${user.first_name} ${user.last_name}`.trim() ||
                                      user.email}
                                  </Typography>
                                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                    {user.email}
                                  </Typography>
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    onClick={handleAddMembers}
                    loading={addMembers.isPending}
                    disabled={selectedUserIds.length === 0}
                  >
                    Add {selectedUserIds.length > 0 ? `(${selectedUserIds.length})` : ''} Members
                  </Button>
                </Stack>
              </Stack>
            )}
          </TabPanel>
        </Tabs>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="plain" color="neutral" onClick={onClose}>
            Close
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default EditGroupModal;
